import { useInfiniteQuery, useQuery } from '@tanstack/react-query';

import { api } from '../../../lib/api-client';

import type { DualApprovalRequestRaw, OverrideRequestRaw, OverrideStatus } from '../types';

export const OVERRIDES_KEY = ['overrides'] as const;

export function useOverrides(status: OverrideStatus): {
  rows: OverrideRequestRaw[];
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  isError: boolean;
} {
  const query = useInfiniteQuery({
    queryKey: [...OVERRIDES_KEY, 'list', status],
    queryFn: ({ pageParam }) =>
      api.listOverrides({
        status,
        ...(pageParam != null ? { cursor: pageParam } : {}),
        limit: 50,
      }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 15 * 1000,
  });
  return {
    rows: (query.data?.pages ?? []).flatMap((p) => p.items),
    fetchNextPage: () => void query.fetchNextPage(),
    hasNextPage: query.hasNextPage,
    isFetchingNextPage: query.isFetchingNextPage,
    isLoading: query.isLoading,
    isError: query.isError,
  };
}

export function useDualApprovalPending(): {
  items: DualApprovalRequestRaw[];
  isLoading: boolean;
} {
  const q = useQuery({
    queryKey: ['dual-approval', 'pending-for-me'],
    queryFn: () => api.dualApprovalPendingForMe(),
    staleTime: 30 * 1000,
  });
  return { items: q.data?.items ?? [], isLoading: q.isLoading };
}
